import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { Component, type OnDestroy, type OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import {
  MatchMediaService,
  PaginationHelperService,
  VTService,
} from '@odin/odin-core';
import {
  PEBLPaymentLink,
  PEBL_SCHEDULE_STATUSES,
  PaymentLinkList,
  type PeblScheduleStatus,
} from '../models/pebl-payment';
import { MatDialog } from '@angular/material/dialog';
import { PeblTrackingDetailsComponent } from '../../pebl-tracking-details/pebl-tracking-details.component';
import { PeblScheduledListComponent } from '../pebl-scheduled-list/pebl-scheduled-list.component';
import { MerchantChangeService } from '@odin/odin-core';
import { AccessGuardService, RoleGuardService } from '@odin/odin-authentication';
import { Subscription } from 'rxjs';
import * as GLOBALS from '../transactions-global.const.values';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { Router } from '@angular/router';

// Enough placeholder rows to fill a tall viewport; any excess is clipped.
const SKELETON_ROW_COUNT = 14;

@Component({
  standalone: false,
  selector: 'odin-pebl-tracking-page',
  templateUrl: './pebl-tracking-page.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./pebl-tracking-page.component.scss'],
})
export class PeblTrackingPageComponent implements OnInit, OnDestroy {
  public rowSize = 62;

  // The CDK defaults (100px / 200px) leave barely a row and a half of runway,
  // so rows were still being created as they crossed the viewport edge — a
  // blank strip at the bottom on the way down and at the top on the way up.
  // Keep six rows rendered beyond each edge, topping back up to twelve.
  public minBufferPx = this.rowSize * 6;
  public maxBufferPx = this.rowSize * 12;

  public virtualScroll: CdkVirtualScrollViewport | null = null;
  private scrollSubscription: Subscription | null = null;

  // The viewport only exists in the 'links' view, so it comes and goes as the
  // view mode is toggled. Binding the scroll listener from the setter keeps it
  // attached to whichever viewport is currently in the DOM — the previous
  // approach re-ran ConfigurePagination from a setTimeout on every toggle
  // without unsubscribing, stacking up another handler on every scroll event
  // each time the user switched back to Links.
  @ViewChild(CdkVirtualScrollViewport, { static: false })
  set virtualScrollViewport(viewport: CdkVirtualScrollViewport | undefined) {
    const next = viewport ?? null;
    if (next === this.virtualScroll) return;

    this.scrollSubscription?.unsubscribe();
    this.scrollSubscription = null;
    this.virtualScroll = next;
    if (next == null) return;

    this.scrollSubscription = next
      .elementScrolled()
      .subscribe(() => this.CheckLoadMore());
  }
  @ViewChild(PeblScheduledListComponent, { static: false })
  scheduledList: PeblScheduledListComponent | null = null;
  // Shown instead of the (empty) viewport while the first page is loading.
  public skeletonRows = Array.from({ length: SKELETON_ROW_COUNT }, (_, i) => i);
  public get showSkeleton(): boolean {
    return this.loading && this.trackingLinks.length === 0;
  }

  public viewMode: 'links' | 'scheduled' = 'links';
  public scheduleStatusFilter: PeblScheduleStatus | undefined;
  public scheduleStatuses: PeblScheduleStatus[] = PEBL_SCHEDULE_STATUSES;
  public trackingLinks: PEBLPaymentLink[] = [];
  public paginationToken: string = '';
  public loading: boolean = false;
  public fetched: boolean = false;
  public moreContent: boolean = true;
  public peblFilters: {
    startDate?: Date;
    endDate?: Date;
    status?: 'Sent' | 'Cancelled' | 'Expired' | 'Authorised';
  } = {
    startDate: undefined,
    endDate: undefined,
    status: undefined,
  };

  private merchantChangeSub: Subscription;

  tabletQuery!: MediaQueryList;
  mobileQuery!: MediaQueryList;
  isTouch = false;

  constructor(
    private accessService: AccessGuardService,
    private vtService: VTService,
    private paginationHelper: PaginationHelperService,
    private dialog: MatDialog,
    private merchantChangeService: MerchantChangeService,
    private mediaService: MatchMediaService,
    private router: Router,
    private roleGuardService: RoleGuardService
  ) {

    if (!this.PermissionChecker()) {
      this.router.navigate(['/dashboard']);
      return;
    }
    
    this.isTouch = this.mediaService.isTouch();
    this.tabletQuery = this.mediaService.matchMedia(GLOBALS.MAX_SCREEN_WIDTH);
    this.mobileQuery = this.mediaService.matchMedia(GLOBALS.MIN_SCREEN_WIDTH);

    this.merchantChangeSub =
      this.merchantChangeService.merchantChangeEvent.subscribe(() => {
        this.RefreshLinks();
        if (!this.PermissionChecker())
          this.accessService.bootNoAccess('Pay By Link');
      });
  }

  public PermissionChecker(): boolean {
    return (
      this.accessService.buidHasFeature('pbl') &&
      (
        this.accessService.hasNewGrant('txn:CreatePebl') ||
        this.roleGuardService.hasPermission('pbl:View')
      )    
    );
  }

  public ngOnInit(): void {
    this.FetchPebl(this.paginationToken);
  }

  public startDateChange(e: MatDatepickerInputEvent<Date>): void {
    if (e.value == undefined) this.peblFilters.startDate = undefined;
    else {
      this.peblFilters.startDate = new Date(e.value);
      this.peblFilters.startDate.setHours(0);
      this.peblFilters.startDate.setMinutes(0);
      this.peblFilters.startDate.setSeconds(0);
    }
    this.filterChange();
  }

  public endDateChange(e: MatDatepickerInputEvent<Date>): void {
    if (e.value == undefined) this.peblFilters.endDate = undefined;
    else {
      this.peblFilters.endDate = new Date(e.value);
      // set to end of today
      this.peblFilters.endDate.setHours(23);
      this.peblFilters.endDate.setMinutes(59);
      this.peblFilters.endDate.setSeconds(59);
    }
    this.filterChange();
  }

  public clearFilters(): void {
    this.peblFilters.endDate = undefined;
    this.peblFilters.startDate = undefined;
    this.peblFilters.status = undefined;
    this.filterChange();
  }

  public Refresh(): void {
    if (this.viewMode === 'scheduled') {
      this.scheduledList?.RefreshSchedules();
      return;
    }
    this.RefreshLinks();
  }

  public onViewModeChange(mode: 'links' | 'scheduled'): void {
    this.viewMode = mode;
  }

  public filterChange(): void {
    this.RefreshLinks();
  }

  ngOnDestroy(): void {
    this.scrollSubscription?.unsubscribe();
    if (this.merchantChangeSub !== undefined)
      this.merchantChangeSub.unsubscribe();
  }

  // Re-checks the scroll position and loads the next page if we're still
  // near the bottom. Called both on scroll events and after a fetch
  // completes, since a scroll event can fire (and get ignored) while a
  // fetch is already in flight - e.g. CDK virtual scroll adjusting the
  // viewport as rows are appended - and no further scroll event is
  // guaranteed to happen afterwards, which left pagination stuck until the
  // user scrolled again.
  private CheckLoadMore(): void {
    if (!this.virtualScroll || this.loading) return;
    if (this.paginationToken === undefined || this.paginationToken === '')
      return;

    const distFromBottom = this.virtualScroll.measureScrollOffset('bottom');
    this.paginationHelper.detectEndOfScrollRaw(
      distFromBottom,
      this.rowSize,
      15,
      () => this.FetchPebl(this.paginationToken),
    );
  }

  private FetchPebl(paginationToken?: string): void {
    if (this.loading) return;
    this.loading = true;
    this.vtService.GetPeblLinks(this.peblFilters, paginationToken).subscribe(
      (_: PaymentLinkList) => {
        this.trackingLinks = [...this.trackingLinks, ..._.paymentRequests];
        this.paginationToken = _.lastEvaluatedKey;
        if (this.paginationToken === '') {
          this.moreContent = false;
        }
        this.loading = false;
        this.fetched = true;
        // CDK virtual scroll can fail to notice the new content height on
        // its own (e.g. growing from an empty list), leaving the results
        // unrendered until something else forces a relayout - so force one
        // straight away rather than leaving the spinner up over real results.
        this.virtualScroll?.checkViewportSize();
        this.CheckLoadMore();
      },
      () => {
        this.fetched = true;
        this.loading = false;
      },
    );
  }

  public RefreshLinks(): void {
    this.trackingLinks = [];
    this.paginationToken = '';
    this.moreContent = true;
    this.fetched = false;
    this.loading = false;
    this.FetchPebl();
  }

  public OpenDetails(trackedLink: PEBLPaymentLink): void {
    this.dialog
      .open(PeblTrackingDetailsComponent, {
        data: trackedLink,
        panelClass: ['main-panel', 'large-modal'],
        autoFocus: false,
      })
      .afterClosed()
      .subscribe((didAction) => {
        if (didAction) this.RefreshLinks();
      });
  }

  public hasExpired(row: PEBLPaymentLink): boolean {
    const expiration = new Date(row.linkExpiration);
    const now = new Date();
    if (expiration < now) return true;
    if (row.status === 'Cancelled') return true;
    if (row.status === 'Expired') return true;
    return false;
  }

  public rowDeliveryIcon(row: PEBLPaymentLink): string {
    switch (row.delivery) {
      case 'email':
        return 'email';
      case 'sms':
        return 'phone_iphone';
      case 'manual':
        return 'assignment_turned_in';
    }
  }

  public rowDeliveryTooltip(row: PEBLPaymentLink): string {
    switch (row.delivery) {
      case 'email':
        return 'E-mail';
      case 'sms':
        return 'SMS';
      case 'manual':
        return 'Copied';
    }
  }

  public rowDeliveryExpiryIcon(row: PEBLPaymentLink): string {
    if (row.status === 'Cancelled') return 'hourglass_disabled';
    const expiration = new Date(row.linkExpiration);
    const now = new Date();
    if (expiration < now) return 'hourglass_disabled';
    if (Math.abs(expiration.getTime() - now.getTime()) / 3600000 < 24)
      return 'hourglass_bottom';
    return 'hourglass_full';
  }

public rowDeliveryExpiryClass(row: PEBLPaymentLink): string {
  if (row.status === 'Authorised') return 'text-success';
  if (row.status === 'Cancelled') return 'text-secondary';

  const expiration = new Date(row.linkExpiration);
  const now = new Date();

  // Expired only if status == Sent
  if (row.status === 'Sent' && expiration < now) return 'text-danger';

  // Warning if it's Sent
  if (
    row.status === 'Sent' &&
    Math.abs(expiration.getTime() - now.getTime()) / 3600000 < 24
  )
    return 'text-warning';

  return 'text-success';
}


  public rowDeliveryPillClass(
    row: PEBLPaymentLink,
    withForeground: boolean,
  ): string {
    const expiration = new Date(row.linkExpiration);
    const now = new Date();

    if (row.status != 'Authorised' && row.status !== 'Cancelled' && expiration < now)
      return `${withForeground ? 'text-white' : 'text-white'} bg-danger`;

    switch (row.status) {
      case 'Sent':
        return `${withForeground ? 'text-black' : 'text-white'} bg-warning`;
      case 'Cancelled':
        return `${withForeground ? 'text-white' : 'text-white'} bg-secondary`;
      case 'Expired':
        return `${withForeground ? 'text-white' : 'text-white'} bg-danger`;
      case 'Authorised':
        return `${withForeground ? 'text-white' : 'text-white'} bg-success`;
    }
  }

  public rowDeliveryPillText(row: PEBLPaymentLink): string {
    const expiration = new Date(row.linkExpiration);
    const now = new Date();

    if (row.status != 'Authorised' && row.status !== 'Cancelled' && expiration < now) return 'Expired';

    switch (row.status) {
      case 'Sent':
        return 'Unpaid';
      case 'Cancelled':
        return 'Cancelled';
      case 'Expired':
        return 'Expired';
      case 'Authorised':
        return 'Paid';
    }
  }

  public rowDeliveryPillIcon(row: PEBLPaymentLink): string {
    const expiration = new Date(row.linkExpiration);
    const now = new Date();

    if (row.status != 'Authorised' && row.status !== 'Cancelled' && expiration < now) return 'cancel';

    switch (row.status) {
      case 'Sent':
        return 'watch_later';
      case 'Cancelled':
        return 'cancel';
      case 'Expired':
        return 'cancel';
      case 'Authorised':
        return 'check_circle';
    }
  }
}
